import React, { ButtonHTMLAttributes, FC } from "react";
import DynamicHeroIcon from "./DynamicHeroIcon";
import type { HeroIcons } from "./DynamicHeroIcon";
import { cva, VariantProps } from "class-variance-authority";
import classNames from "classnames";
import { Transition } from "@headlessui/react";

const buttonSolidCVA = cva("disabled:opacity-75 disabled:cursor-not-allowed", {
  variants: {
    fullWidth: {
      true: "flex w-full items-center justify-center",
      false: "inline-flex items-center",
    },
    variant: {
      default:
        "bg-primary-700 text-white enabled:hover:bg-primary-800    focus:ring-primary-300 dark:bg-primary-600 dark:enabled:hover:bg-primary-700 dark:focus:ring-primary-800",
      alternative:
        "border border-gray-200 bg-white text-gray-900 enabled:hover:bg-gray-100 hover:text-primary-700 focus:z-10   focus:ring-gray-200 dark:border-gray-500 dark:bg-gray-600 dark:text-gray-50 dark:enabled:hover:bg-gray-700 dark:hover:text-white dark:focus:ring-gray-700",
      dark: "bg-gray-700  text-white enabled:hover:bg-gray-900   focus:ring-gray-300 dark:border-gray-700 dark:bg-gray-600 dark:enabled:enabled:hover:bg-gray-700 dark:focus:ring-gray-700",
      white:
        "border border-gray-300 bg-white  text-gray-900 enabled:hover:bg-gray-100  focus:ring-gray-200  dark:border-gray-400 dark:bg-gray-600 dark:text-white dark:hover:border-gray-600 dark:enabled:hover:bg-gray-700 dark:focus:ring-gray-700",
      green:
        " bg-green-700  text-white enabled:hover:bg-green-800   focus:ring-green-300  dark:bg-green-600 dark:enabled:hover:bg-green-700 dark:focus:ring-green-800",
      red: " bg-red-700  text-white enabled:hover:bg-red-800   focus:ring-red-300  dark:bg-red-600 dark:enabled:hover:bg-red-700 dark:focus:ring-red-900",
    },
    size: {
      xs: "px-3 py-2 text-xs",
      sm: "px-3 py-2 text-sm",
      base: "px-5 py-2.5 text-sm",
      lg: "px-5 py-3 text-base",
      xl: "px-6 py-3.5 text-base",
    },
    enableAnimation: {
      true: "transition delay-150 ease-in-out hover:-translate-y-1 hover:scale-110 hover:transition-all",
    },
  },
  compoundVariants: [
    {
      variant: ["default", "alternative", "dark", "white", "green", "red"],
      className: "rounded-lg font-medium focus:outline-none focus:ring-4",
    },
  ],
  defaultVariants: {
    size: "base",
    fullWidth: false,
    variant: "default",
    enableAnimation: false,
  },
});

const buttonOutlineCVA = cva(
  "inline-flex items-center disabled:opacity-75 disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        default:
          " border-primary-700 text-primary-700 enabled:hover:bg-primary-800 enabled:hover:text-white focus:ring-primary-300 dark:border-primary-500 dark:text-primary-500 dark:enabled:hover:bg-primary-600 dark:enabled:hover:text-white dark:focus:ring-primary-800",
        dark: "border-gray-700 text-gray-700 enabled:hover:bg-gray-800 enabled:hover:text-white focus:ring-gray-300 dark:border-gray-500 dark:text-gray-500 dark:enabled:hover:bg-gray-600 dark:enabled:hover:text-white dark:focus:ring-gray-800",

        green:
          " border-green-700 text-green-700 enabled:hover:bg-green-800 enabled:hover:text-white focus:ring-green-300 dark:border-green-500 dark:text-green-500 dark:enabled:hover:bg-green-600 dark:enabled:hover:text-white dark:focus:ring-green-800",
        red: " border-red-700 text-red-700 enabled:hover:bg-red-800 enabled:hover:text-white focus:ring-red-300 dark:border-red-500 dark:text-red-500 dark:enabled:hover:bg-red-600 dark:enabled:hover:text-white dark:focus:ring-red-800",
      },
      size: {
        xs: "px-3 py-2 text-xs",
        sm: "px-3 py-2 text-sm",
        base: "px-5 py-2.5 text-sm",
        lg: "px-5 py-3 text-base",
        xl: "px-6 py-3.5 text-base",
      },
      enableAnimation: {
        true: "transition delay-150 ease-in-out hover:-translate-y-1 hover:scale-110 hover:transition-all",
      },
    },
    compoundVariants: [
      {
        variant: ["default", "dark", "green", "red"],
        className:
          "border rounded-lg font-medium focus:outline-none focus:ring-4 text-center",
      },
    ],
    defaultVariants: {
      size: "base",
      variant: "default",
      enableAnimation: false,
    },
  }
);

const buttonIconCVA = cva("", {
  variants: {
    size: {
      xs: "h-4 w-4",
      sm: "h-4 w-4",
      base: "h-5 w-5",
      md: "h-5 w-5",
      lg: "h-5 w-5",
      xl: "h-5 w-5",
    },
  },
  defaultVariants: {
    size: "base",
  },
});

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  leftIcon?: HeroIcons;
  rightIcon?: HeroIcons;
  isLoading?: boolean;
}

interface ButtonSolidProps
  extends ButtonProps,
    VariantProps<typeof buttonSolidCVA> {}

const ButtonSolid: FC<ButtonSolidProps> = ({
  className,
  variant,
  size,
  leftIcon = undefined,
  rightIcon = undefined,
  enableAnimation = false,
  isLoading = false,
  type = "button",
  label,
  fullWidth,
  ...rest
}) => {
  return (
    <button
      type={type}
      className={buttonSolidCVA({
        size,
        variant,
        enableAnimation,
        fullWidth,
        className,
      })}
      {...rest}
    >
      <Transition show={isLoading} appear={true}>
        <div role="status">
          <svg
            aria-hidden="true"
            className={buttonIconCVA({
              size,
              className:
                "animate-spin fill-primary-600 text-gray-200 dark:text-gray-600",
            })}
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
          <span className="sr-only">Loading...</span>
        </div>
      </Transition>
      <div
        className={classNames("flex-row items-center justify-center gap-x-2", {
          hidden: isLoading,
          flex: !isLoading,
        })}
      >
        {leftIcon ? (
          <DynamicHeroIcon
            icon={leftIcon}
            className={buttonIconCVA({ size })}
          />
        ) : null}
        {label}
        {rightIcon ? (
          <DynamicHeroIcon
            icon={rightIcon}
            className={buttonIconCVA({ size })}
          />
        ) : null}
      </div>
    </button>
  );
};

interface ButtonOutlineProps
  extends ButtonProps,
    VariantProps<typeof buttonOutlineCVA> {}

const ButtonOutline: FC<ButtonOutlineProps> = ({
  className,
  variant,
  size,
  leftIcon = undefined,
  rightIcon = undefined,
  enableAnimation = false,
  isLoading = false,
  type = "button",
  label,
  ...rest
}) => {
  return (
    <button
      type={type}
      className={buttonOutlineCVA({
        size,
        variant,
        enableAnimation,
        className,
      })}
      {...rest}
    >
      <Transition show={isLoading} appear={true}>
        <div role="status">
          <svg
            aria-hidden="true"
            className={buttonIconCVA({
              size,
              className:
                "animate-spin fill-primary-600 text-gray-200 dark:text-gray-600",
            })}
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
          <span className="sr-only">Loading...</span>
        </div>
      </Transition>
      <div
        className={classNames(
          "flex flex-row items-center justify-center gap-x-2",
          { hidden: isLoading, flex: !isLoading }
        )}
      >
        {leftIcon ? (
          <DynamicHeroIcon
            icon={leftIcon}
            className={buttonIconCVA({ size })}
          />
        ) : null}
        {label}
        {rightIcon ? (
          <DynamicHeroIcon
            icon={rightIcon}
            className={buttonIconCVA({ size })}
          />
        ) : null}
      </div>
    </button>
  );
};

export { ButtonSolid, ButtonOutline };
